import {
    IMappingValidationResult,
    ISpreadsheetTemplateActive,
    ISpreadsheetTemplateGetMappingResult,
    IWasteReturnFixedCellMapping,
    IWasteReturnListSourceDefinition,
    IWasteReturnListSourceMapping,
    IWasteReturnPairedColumnInputMapping,
    IWasteReturnTableColumnMapping,
    IWasteReturnTableMapping,
    IWasteReturnTemplateMapping,
    IWorkbookInspection,
    SpreadsheetTemplateType,
    WasteReturnFixedCellKeys,
    WasteReturnOverflowStrategies,
    WasteReturnPairedColumnInputKeys,
    WasteReturnRegFieldKeys,
    WasteReturnRowModes,
    WasteReturnTableColumnKeys,
    WasteReturnValueFormatters,
} from "../../customTypings/spreadsheetTemplate/SpreadsheetTemplate";
import { EA_LIST_SOURCE_DEFAULTS } from "./eaListSourceDefaults";
import { coerceMappingValidationResult } from "./mappingValidation";
import { formatListSourceColOpenValueRange, buildGeographyAdjacentColumnMap } from "./mappingRangeAddress";
import { resolvePrimaryValueField } from "./listSourcePrimaryValueField";

/** Matches backend default `SpreadsheetTemplate:MaxFileSizeBytes`. */
export const EA_WASTE_RETURN_MAX_UPLOAD_BYTES = 10_485_760;

export const EA_WASTE_RETURN_TEMPLATE_REPLACE_WARNING =
    "Uploading a new template will replace the active EA Waste Return template and may require the mapping to be reviewed.";

export function validateTemplateUploadFile(
    file: File,
    maxBytes: number = EA_WASTE_RETURN_MAX_UPLOAD_BYTES
): string | null {
    const extension = file.name.includes(".") ? file.name.slice(file.name.lastIndexOf(".")).toLowerCase() : "";
    if (extension !== ".xlsx") {
        return "Only .xlsx workbooks are supported.";
    }

    if (file.size <= 0) {
        return "Uploaded file is empty.";
    }

    if (file.size > maxBytes) {
        return `File exceeds the maximum size of ${formatFileSize(maxBytes)}.`;
    }

    return null;
}

export const REG_FIELD_KEYS_REQUIRING_LIST_SOURCE: string[] = [
    WasteReturnRegFieldKeys.Ewc,
    WasteReturnRegFieldKeys.EaDrReceived,
    WasteReturnRegFieldKeys.EaDrRemoved,
    WasteReturnRegFieldKeys.PhysicalState,
    WasteReturnRegFieldKeys.Municipal,
    WasteReturnRegFieldKeys.Degradable,
    WasteReturnRegFieldKeys.SourceActivity,
    WasteReturnRegFieldKeys.PreTreatment,
    WasteReturnRegFieldKeys.Geography,
    WasteReturnRegFieldKeys.FacilityOperation,
    WasteReturnRegFieldKeys.TechnicalCompetence,
];

/** Reg fields excluded from synchronise comparison cards (admin-managed elsewhere). */
export const REG_FIELD_KEYS_EXCLUDED_FROM_COMPARISON: readonly string[] = [
    WasteReturnRegFieldKeys.Municipal,
    WasteReturnRegFieldKeys.Degradable,
];

export const REG_FIELD_KEYS_FOR_COMPARISON: string[] = REG_FIELD_KEYS_REQUIRING_LIST_SOURCE.filter(
    (fieldKey) => !REG_FIELD_KEYS_EXCLUDED_FROM_COMPARISON.includes(fieldKey)
);

export const GEOGRAPHY_ADJACENT_COLUMNS = ["code", "description", "type"] as const;

export const FIXED_CELL_FIELD_KEYS = Object.values(WasteReturnFixedCellKeys);

export const WASTE_RECEIVED_COLUMN_KEYS = [
    WasteReturnTableColumnKeys.Origin,
    WasteReturnTableColumnKeys.EwcCode,
    WasteReturnTableColumnKeys.DisposalRecoveryCode,
    WasteReturnTableColumnKeys.MunicipalFlag,
    WasteReturnTableColumnKeys.DegradableFlag,
    WasteReturnTableColumnKeys.PhysicalState,
    WasteReturnTableColumnKeys.SourceActivity,
    WasteReturnTableColumnKeys.AmountTonnes,
    WasteReturnTableColumnKeys.PreTreatment,
] as const;

export const WASTE_REMOVED_COLUMN_KEYS = [
    WasteReturnTableColumnKeys.Destination,
    WasteReturnTableColumnKeys.EwcCode,
    WasteReturnTableColumnKeys.MunicipalFlag,
    WasteReturnTableColumnKeys.PhysicalState,
    WasteReturnTableColumnKeys.DisposalRecoveryCode,
    WasteReturnTableColumnKeys.AmountTonnes,
] as const;

function fixedCell(
    sheet: string,
    address: string,
    valueFormatter: string,
    regFieldKey?: string,
    maxLength?: number
): IWasteReturnFixedCellMapping {
    return { sheet, address, valueFormatter, regFieldKey: regFieldKey ?? null, maxLength: maxLength ?? null };
}

function column(column: string, valueFormatter: string, regFieldKey?: string): IWasteReturnTableColumnMapping {
    return { column, valueFormatter, regFieldKey: regFieldKey ?? null };
}

function receivedTable(): IWasteReturnTableMapping {
    return {
        sheet: "Waste_Received",
        firstDataRow: 8,
        rowMode: WasteReturnRowModes.FixedRange,
        maxRows: 10000,
        overflowStrategy: WasteReturnOverflowStrategies.Fail,
        columns: {
            [WasteReturnTableColumnKeys.Origin]: column("B", WasteReturnValueFormatters.Description, WasteReturnRegFieldKeys.Geography),
            [WasteReturnTableColumnKeys.EwcCode]: column("C", WasteReturnValueFormatters.CodeDescription, WasteReturnRegFieldKeys.Ewc),
            [WasteReturnTableColumnKeys.DisposalRecoveryCode]: column("D", WasteReturnValueFormatters.Code, WasteReturnRegFieldKeys.EaDrReceived),
            [WasteReturnTableColumnKeys.MunicipalFlag]: column("E", WasteReturnValueFormatters.YesNo, WasteReturnRegFieldKeys.Municipal),
            [WasteReturnTableColumnKeys.DegradableFlag]: column("F", WasteReturnValueFormatters.YesNo, WasteReturnRegFieldKeys.Degradable),
            [WasteReturnTableColumnKeys.PhysicalState]: column("G", WasteReturnValueFormatters.Description, WasteReturnRegFieldKeys.PhysicalState),
            [WasteReturnTableColumnKeys.SourceActivity]: column("H", WasteReturnValueFormatters.Description, WasteReturnRegFieldKeys.SourceActivity),
            [WasteReturnTableColumnKeys.AmountTonnes]: column("I", WasteReturnValueFormatters.Decimal),
            [WasteReturnTableColumnKeys.PreTreatment]: column("J", WasteReturnValueFormatters.Description, WasteReturnRegFieldKeys.PreTreatment),
        },
    };
}

function removedTable(): IWasteReturnTableMapping {
    return {
        sheet: "Waste_Removed",
        firstDataRow: 8,
        rowMode: WasteReturnRowModes.FixedRange,
        maxRows: 10000,
        overflowStrategy: WasteReturnOverflowStrategies.Fail,
        columns: {
            [WasteReturnTableColumnKeys.Destination]: column("B", WasteReturnValueFormatters.Description, WasteReturnRegFieldKeys.Geography),
            [WasteReturnTableColumnKeys.EwcCode]: column("C", WasteReturnValueFormatters.CodeDescription, WasteReturnRegFieldKeys.Ewc),
            [WasteReturnTableColumnKeys.MunicipalFlag]: column("D", WasteReturnValueFormatters.YesNo, WasteReturnRegFieldKeys.Municipal),
            [WasteReturnTableColumnKeys.PhysicalState]: column("E", WasteReturnValueFormatters.Description, WasteReturnRegFieldKeys.PhysicalState),
            [WasteReturnTableColumnKeys.DisposalRecoveryCode]: column("F", WasteReturnValueFormatters.Code, WasteReturnRegFieldKeys.EaDrRemoved),
            [WasteReturnTableColumnKeys.AmountTonnes]: column("G", WasteReturnValueFormatters.Decimal),
        },
    };
}

function listSourceAutoDetected(
    sheet: string,
    column: string,
    startRow: number,
    endRow: number,
    regFieldKey: string
): IWasteReturnListSourceMapping {
    const valueRange = formatListSourceColOpenValueRange(column, startRow);
    return {
        autoDetected: {
            sourceType: "directRange",
            sheet,
            valueRange,
            primaryValueField: resolvePrimaryValueField(regFieldKey),
            adjacentColumnMap:
                regFieldKey === WasteReturnRegFieldKeys.Geography
                    ? buildGeographyAdjacentColumnMap(valueRange)
                    : {},
        },
        extractedValues: [],
    };
}

function listSourceNamedRange(
    sheet: string,
    rangeName: string,
    regFieldKey: string
): IWasteReturnListSourceMapping {
    const definition: IWasteReturnListSourceDefinition = {
        sourceType: "namedRange",
        sheet,
        valueRange: rangeName,
        primaryValueField: resolvePrimaryValueField(regFieldKey),
        adjacentColumnMap: {},
    };
    return {
        autoDetected: definition,
        manualOverride: definition,
        extractedValues: [],
    };
}

export function createDefaultEaWasteReturnMapping(): IWasteReturnTemplateMapping {
    return {
        schemaVersion: 1,
        templateType: SpreadsheetTemplateType.EaWasteReturn,
        fixedCells: {
            [WasteReturnFixedCellKeys.SiteOperatorName]: fixedCell("Site_Information", "B24", WasteReturnValueFormatters.Raw),
            [WasteReturnFixedCellKeys.PermitNumber]: fixedCell("Site_Information", "B26", WasteReturnValueFormatters.Raw, undefined, 5),
            [WasteReturnFixedCellKeys.SiteName]: fixedCell("Site_Information", "B28", WasteReturnValueFormatters.Raw),
            [WasteReturnFixedCellKeys.SiteAddress]: fixedCell("Site_Information", "B30:E33", WasteReturnValueFormatters.Raw),
            [WasteReturnFixedCellKeys.SitePostcode]: fixedCell("Site_Information", "C34", WasteReturnValueFormatters.Raw),
            [WasteReturnFixedCellKeys.WeighbridgeUsed]: fixedCell("Site_Information", "D40", WasteReturnValueFormatters.YesNo),
            [WasteReturnFixedCellKeys.PercentageWeighed]: fixedCell("Site_Information", "D41", WasteReturnValueFormatters.Decimal),
            [WasteReturnFixedCellKeys.FormComplete]: fixedCell("Site_Information", "H39", WasteReturnValueFormatters.YesNo),
            [WasteReturnFixedCellKeys.SignatoryName]: fixedCell("Site_Information", "H40", WasteReturnValueFormatters.Raw),
            [WasteReturnFixedCellKeys.SignatoryPosition]: fixedCell("Site_Information", "H41", WasteReturnValueFormatters.Raw),
            [WasteReturnFixedCellKeys.SignatoryPhone]: fixedCell("Site_Information", "H42", WasteReturnValueFormatters.Raw),
            [WasteReturnFixedCellKeys.NilReturn]: fixedCell("Site_Information", "K40", WasteReturnValueFormatters.YesNo),
            [WasteReturnFixedCellKeys.FormCompletionDate]: fixedCell("Site_Information", "K42", WasteReturnValueFormatters.Date),
            [WasteReturnFixedCellKeys.ReportingPeriod]: fixedCell("Site_Information", "B19", WasteReturnValueFormatters.Raw),
            [WasteReturnFixedCellKeys.ReportingYear]: fixedCell("Site_Information", "D19", WasteReturnValueFormatters.Raw),
            [WasteReturnFixedCellKeys.FacilityOperation]: fixedCell(
                "Site_Information",
                "B37",
                WasteReturnValueFormatters.CodeDescription,
                WasteReturnRegFieldKeys.FacilityOperation
            ),
            [WasteReturnFixedCellKeys.CompetenceScheme]: fixedCell(
                "Technical Competence",
                "I2",
                WasteReturnValueFormatters.Code,
                WasteReturnRegFieldKeys.TechnicalCompetence
            ),
        },
        tables: {
            wasteReceived: receivedTable(),
            wasteRemoved: removedTable(),
        },
        listSources: {
            [WasteReturnRegFieldKeys.Geography]: listSourceAutoDetected(
                EA_LIST_SOURCE_DEFAULTS.geography.sheet,
                EA_LIST_SOURCE_DEFAULTS.geography.column,
                EA_LIST_SOURCE_DEFAULTS.geography.startRow,
                EA_LIST_SOURCE_DEFAULTS.geography.endRow,
                WasteReturnRegFieldKeys.Geography
            ),
            [WasteReturnRegFieldKeys.Ewc]: listSourceAutoDetected(
                EA_LIST_SOURCE_DEFAULTS.ewc.sheet,
                EA_LIST_SOURCE_DEFAULTS.ewc.column,
                EA_LIST_SOURCE_DEFAULTS.ewc.startRow,
                EA_LIST_SOURCE_DEFAULTS.ewc.endRow,
                WasteReturnRegFieldKeys.Ewc
            ),
            [WasteReturnRegFieldKeys.EaDrReceived]: listSourceAutoDetected(
                EA_LIST_SOURCE_DEFAULTS.disposalRecoveryReceived.sheet,
                EA_LIST_SOURCE_DEFAULTS.disposalRecoveryReceived.column,
                EA_LIST_SOURCE_DEFAULTS.disposalRecoveryReceived.startRow,
                EA_LIST_SOURCE_DEFAULTS.disposalRecoveryReceived.endRow,
                WasteReturnRegFieldKeys.EaDrReceived
            ),
            [WasteReturnRegFieldKeys.EaDrRemoved]: listSourceAutoDetected(
                EA_LIST_SOURCE_DEFAULTS.disposalRecoveryRemoved.sheet,
                EA_LIST_SOURCE_DEFAULTS.disposalRecoveryRemoved.column,
                EA_LIST_SOURCE_DEFAULTS.disposalRecoveryRemoved.startRow,
                EA_LIST_SOURCE_DEFAULTS.disposalRecoveryRemoved.endRow,
                WasteReturnRegFieldKeys.EaDrRemoved
            ),
            [WasteReturnRegFieldKeys.FacilityOperation]: {
                autoDetected: {
                    sourceType: "namedRange",
                    sheet: "Look Up Values",
                    valueRange: null,
                    primaryValueField: "description",
                    adjacentColumnMap: {},
                },
                manualOverride: {
                    sourceType: "directRange",
                    sheet: EA_LIST_SOURCE_DEFAULTS.facilityOperation.sheet,
                    valueRange: formatListSourceColOpenValueRange(
                        EA_LIST_SOURCE_DEFAULTS.facilityOperation.column,
                        EA_LIST_SOURCE_DEFAULTS.facilityOperation.startRow
                    ),
                    primaryValueField: "description",
                    adjacentColumnMap: {},
                },
                extractedValues: [],
            },
            [WasteReturnRegFieldKeys.TechnicalCompetence]: listSourceNamedRange(
                EA_LIST_SOURCE_DEFAULTS.technicalCompetence.sheet,
                "TCM_List",
                WasteReturnRegFieldKeys.TechnicalCompetence
            ),
        },
        pairedColumnInputs: {
            [WasteReturnPairedColumnInputKeys.TcManagers]: createDefaultTcManagersPairedColumnInput(),
        },
    };
}

export function createDefaultTcManagersPairedColumnInput(): IWasteReturnPairedColumnInputMapping {
    return {
        names: {
            sheet: "Technical Competence",
            address: "H11:H16",
        },
        dobs: {
            sheet: "Technical Competence",
            address: "I11:I16",
        },
    };
}

export function createFreshImportEaWasteReturnMapping(): IWasteReturnTemplateMapping {
    const mapping = createDefaultEaWasteReturnMapping();
    const listSources: IWasteReturnTemplateMapping["listSources"] = {};
    for (const fieldKey of REG_FIELD_KEYS_REQUIRING_LIST_SOURCE) {
        listSources[fieldKey] = { extractedValues: [] };
    }
    return { ...mapping, listSources };
}

export function ensureListSources(mapping: IWasteReturnTemplateMapping): IWasteReturnTemplateMapping {
    const listSources = { ...mapping.listSources };
    for (const fieldKey of REG_FIELD_KEYS_REQUIRING_LIST_SOURCE) {
        if (!listSources[fieldKey]) {
            listSources[fieldKey] = { extractedValues: [] };
        }
    }
    return ensurePairedColumnInputs({ ...mapping, listSources });
}

export function ensurePairedColumnInputs(mapping: IWasteReturnTemplateMapping): IWasteReturnTemplateMapping {
    const pairedColumnInputs = { ...(mapping.pairedColumnInputs ?? {}) };
    if (!pairedColumnInputs[WasteReturnPairedColumnInputKeys.TcManagers]) {
        pairedColumnInputs[WasteReturnPairedColumnInputKeys.TcManagers] = createDefaultTcManagersPairedColumnInput();
    }
    return { ...mapping, pairedColumnInputs };
}

export function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function shouldWarnBeforeTemplateReplace(hasActiveTemplate: boolean): boolean {
    return hasActiveTemplate;
}

export function getOverflowStrategyOptions(): string[] {
    return [WasteReturnOverflowStrategies.Fail];
}

export function getRowModeOptions(): string[] {
    return [WasteReturnRowModes.FixedRange];
}

export function getValueFormatterOptions(): string[] {
    return Object.values(WasteReturnValueFormatters);
}

export function createEmptyListSourceDefinition(): IWasteReturnListSourceMapping {
    return {
        manualOverride: {
            sourceType: "directRange",
            sheet: "",
            valueRange: "",
            primaryValueField: "description",
            adjacentColumnMap: {},
        },
        extractedValues: [],
    };
}

export function canSubmitTemplateUpload(params: {
    hasFile: boolean;
    showReplaceWarning: boolean;
    confirmReplace: boolean;
}): boolean {
    if (!params.hasFile) return false;
    if (params.showReplaceWarning && !params.confirmReplace) return false;
    return true;
}

export function isActiveTemplateNotFoundError(message: string): boolean {
    return message.toLowerCase().includes("not found");
}

export function getWorkbookInspectionCounts(inspection: IWorkbookInspection) {
    return {
        worksheetCount: inspection.workbook.worksheetCount,
        namedRangeCount: inspection.workbook.namedRangeCount,
        dataValidationCount: inspection.workbook.dataValidationCount,
        warningCount: inspection.workbook.warningCount,
        worksheetNames: inspection.worksheets.map((sheet) => sheet.name),
        namedRangeNames: inspection.namedRanges.map((range) => range.name),
        warningCodes: inspection.warnings.map((warning) => warning.code),
    };
}

export function hasBlockingValidationErrors(validation: IMappingValidationResult | null | undefined): boolean {
    const parsed = coerceMappingValidationResult(validation);
    return !!parsed && parsed.errors.length > 0;
}

export function hasValidationWarnings(validation: IMappingValidationResult | null | undefined): boolean {
    const parsed = coerceMappingValidationResult(validation);
    return !!parsed && parsed.warnings.length > 0;
}

export function applyStoredMappingReadResponse(
    response: ISpreadsheetTemplateGetMappingResult,
    fallbackMapping: () => IWasteReturnTemplateMapping = createDefaultEaWasteReturnMapping
): {
    template: ISpreadsheetTemplateActive;
    mapping: IWasteReturnTemplateMapping;
    validation: IMappingValidationResult | null;
} {
    return {
        template: response.template,
        mapping: ensureListSources(response.mapping ?? fallbackMapping()),
        validation: coerceMappingValidationResult(response.validation),
    };
}
